import React, { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { User, MapPin, Phone, Save, Check, AlertCircle } from "lucide-react";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA",
  "ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK",
  "OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.name || "",
        phone: user.phone || "",
        address_line1: user.address_line1 || "",
        address_line2: user.address_line2 || "",
        city: user.city || "",
        state: user.state || "",
        postal_code: user.postal_code || "",
      });
    }
  }, [user]);

  const isComplete =
    form.full_name && form.address_line1 && form.city && form.state && form.postal_code;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me/profile", form);
      setUser(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save your profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="section dark-zone">
      <div className="container-if max-w-2xl">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-[var(--if-gold)]" />
          <span className="font-mono-label text-[var(--if-gold)]">Operator File</span>
        </div>
        <h1 className="font-display text-[40px] md:text-[56px] leading-[0.95] mb-3">
          Your <span className="italic text-[var(--if-gold)]">profile</span>.
        </h1>
        <p className="text-[rgba(35,38,28,0.65)] mb-8" style={{ fontFamily: "Inter, sans-serif" }}>
          Add your mailing address so Sergeant Credit can auto-format your dispute letters with the correct sender info.
        </p>

        {!isComplete && (
          <div className="card-dark p-4 mb-6 flex items-start gap-3 border-[var(--if-gold)]/40" data-testid="profile-incomplete-banner">
            <AlertCircle className="w-5 h-5 text-[var(--if-gold)] shrink-0 mt-0.5" />
            <div>
              <div className="font-mono-label text-[var(--if-gold)] mb-1">Address required for disputes</div>
              <p className="text-sm text-white/70" style={{ fontFamily: "Inter, sans-serif" }}>
                You'll need a complete mailing address (street, city, state, ZIP) before generating dispute letter PDFs.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="card-dark p-6 flex flex-col gap-5" data-testid="profile-form">
          <div>
            <label className="font-mono-label text-white/55 block mb-2">Full legal name</label>
            <input
              className="if-input"
              placeholder="As it appears on your credit report"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="profile-name"
              required
            />
          </div>

          <div>
            <label className="font-mono-label text-white/55 block mb-2">
              <Phone className="w-3 h-3 inline mr-1" /> Phone (optional)
            </label>
            <input
              className="if-input"
              placeholder="+1 (555) 123-4567"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="profile-phone"
            />
          </div>

          <div className="border-t border-white/10 pt-5">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-[var(--if-gold)]" />
              <span className="font-mono-label text-[var(--if-gold)]">Mailing Address</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="font-mono-label text-white/55 block mb-2">Street address</label>
                <input
                  className="if-input"
                  placeholder="123 Main St"
                  value={form.address_line1}
                  onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                  data-testid="profile-address1"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label text-white/55 block mb-2">Apt / Suite (optional)</label>
                <input
                  className="if-input"
                  placeholder="Apt 4B"
                  value={form.address_line2}
                  onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                  data-testid="profile-address2"
                />
              </div>
              <div>
                <label className="font-mono-label text-white/55 block mb-2">City</label>
                <input
                  className="if-input"
                  placeholder="Austin"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="profile-city"
                />
              </div>
              <div className="grid grid-cols-[1fr,1fr] gap-3">
                <div>
                  <label className="font-mono-label text-white/55 block mb-2">State</label>
                  <select
                    className="if-input"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    data-testid="profile-state"
                  >
                    <option value="">—</option>
                    {US_STATES.map((s) => <option key={s} value={s} className="bg-white">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-mono-label text-white/55 block mb-2">ZIP</label>
                  <input
                    className="if-input"
                    placeholder="78701"
                    maxLength={10}
                    value={form.postal_code}
                    onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                    data-testid="profile-zip"
                  />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-300 flex items-center gap-2" data-testid="profile-error">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-gold" data-testid="profile-save">
              {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saved ? "Saved" : saving ? "Saving…" : "Save profile"}
            </button>
            {isComplete && <span className="text-xs text-green-400" style={{ fontFamily: "Inter, sans-serif" }}>Address complete · ready for disputes</span>}
          </div>
        </form>

        <div className="mt-6 text-xs text-white/40" style={{ fontFamily: "Inter, sans-serif" }}>
          Logged in as <span className="text-white/70">{user?.email}</span> · Account created {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
        </div>
      </div>
    </section>
  );
}
